import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import useStoreSettings from "@/hooks/useStoreSettings";
import { updateSystemSettings } from "@/services/supabaseService";
import AdminLayout from "@/components/feature/AdminLayout";

export default function Settings() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { settings, refresh } = useStoreSettings();
  const [lineUrl, setLineUrl] = useState("https://line.me/ti/p/-xtd_8xuUf");
  const [settingsMsg, setSettingsMsg] = useState("");

  useEffect(() => {
    setLineUrl(settings.site_line_url || "https://line.me/ti/p/-xtd_8xuUf");
  }, [settings.site_line_url]);

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-forbid-line text-4xl text-gray-300"></i>
          </div>
          <p className="text-gray-500 text-lg mb-4">權限不足</p>
          <p className="text-sm text-gray-400 mb-6">只有管理員才能訪問此頁面</p>
          <button onClick={() => navigate("/")} className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium cursor-pointer">返回首頁</button>
        </div>
      </div>
    );
  }

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSettingsMsg("");
    const trimmed = lineUrl.trim();
    if (!trimmed) { setSettingsMsg("請輸入有效的 LINE 連結"); return; }
    try {
      await updateSystemSettings({ site_line_url: trimmed });
      await refresh();
      setSettingsMsg("LINE 團長連結已更新！全站連結已同步生效。");
    } catch (err: unknown) {
      let msg = "更新失敗，請稍後再試";
      if (err instanceof Error) msg = err.message;
      else if (typeof err === "string") msg = err;
      console.error("[Settings] 更新失敗:", err);
      setSettingsMsg(msg);
    }
  };

  const adminNavCards = [
    { label: "儲值審核", href: "/admin/recharge", icon: "ri-wallet-3-line", color: "bg-amber-50 text-amber-600" },
    { label: "商品管理", href: "/admin/products", icon: "ri-shopping-bag-3-line", color: "bg-blue-50 text-blue-600" },
    { label: "訂單管理", href: "/admin/orders", icon: "ri-truck-line", color: "bg-emerald-50 text-emerald-600" },
    { label: "會員管理", href: "/admin/members", icon: "ri-team-line", color: "bg-purple-50 text-purple-600" },
  ];

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <button onClick={() => navigate("/admin/dashboard")} className="hover:text-amber-600 cursor-pointer">管理後台</button>
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></div>
          <span className="text-gray-700">系統設置</span>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-5 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center"><i className="ri-settings-3-line text-amber-600 text-sm"></i></div>
            系統設置
          </h2>
          <form onSubmit={handleSaveSettings} className="space-y-4">
            <div>
              <label className="text-sm text-gray-600 mb-1.5 block">團長 LINE 客服連結</label>
              <input type="url" value={lineUrl} onChange={(e) => { setLineUrl(e.target.value); setSettingsMsg(""); }} placeholder="https://line.me/..." className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400" />
              <p className="text-xs text-gray-400 mt-1.5">此連結將自動同步至全站所有 LINE 相關按鈕（頁尾「聯絡我們」、儲值頁面「聯繫團長」、首頁客服入口）。未設定時預設為 https://line.me。</p>
            </div>
            {settingsMsg && (
              <div className={`rounded-lg px-3 py-2.5 text-sm border shadow-sm ${settingsMsg.includes("更新") ? "bg-emerald-50 border-emerald-200 text-emerald-600" : "bg-red-50 border-red-200 text-red-600"}`}>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center"><i className={`${settingsMsg.includes("更新") ? "ri-check-line" : "ri-error-warning-line"} text-xs`}></i></div>
                  {settingsMsg}
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button type="submit" className="px-5 py-2.5 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap">儲存設定</button>
              <button type="button" onClick={() => { setLineUrl(settings.site_line_url || "https://line.me/ti/p/-xtd_8xuUf"); setSettingsMsg(""); }} className="px-5 py-2.5 rounded-lg bg-gray-100 text-gray-600 text-sm hover:bg-gray-200 cursor-pointer whitespace-nowrap">還原預設</button>
            </div>
          </form>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line text-gray-400 text-xs"></i></div>
            即時預覽
          </h3>
          <div className="bg-stone-900 rounded-xl p-5">
            <p className="text-stone-400 text-sm mb-4">聯繫團長：吉時小助手</p>
            <a href={settings.site_line_url || "https://line.me"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#06C755] hover:bg-[#05b34d] text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap">
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-line-fill text-base"></i></div>
              立即私訊團長 (LINE)
            </a>
          </div>
          <p className="text-xs text-gray-400 mt-3">上方按鈕跳轉目標即為目前儲存的連結</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {adminNavCards.map((card) => (
            <button key={card.label} onClick={() => navigate(card.href)} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer flex flex-col items-center gap-2">
              <div className={`w-9 h-9 rounded-lg ${card.color} flex items-center justify-center`}><i className={`${card.icon} text-lg`}></i></div>
              <span className="text-xs font-medium text-gray-700 whitespace-nowrap">{card.label}</span>
            </button>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}